import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FirePatrolCrewInput, FirePatrolRole } from '@jupi/shared'
import { api } from '@jupi/ui'

export interface FirePatrolShift {
  shiftId: number
  shiftDate: string
  userId: string
  displayName: string
  role: FirePatrolRole | null
  note: string | null
  lockedAt: string | null
  lockedBy: string | null
}

export interface FirePatrolWeek {
  weekStart: string
  dispatcherUserId: string
  dispatcherName: string
}

export interface FirePatrolBooking {
  bookingId: number
  registration: string
  aircraftType: string | null
  displayName: string
  kind: 'reservation' | 'option'
  startsAt: string
  endsAt: string
  comment: string | null
  // The run it was booked in; releasing the run releases them all.
  bulkGroupId: string | null
}

export interface FirePatrolCalendar {
  shifts: FirePatrolShift[]
  weeks: FirePatrolWeek[]
  bookings: FirePatrolBooking[]
}

export interface FirePatrolCrewMember extends FirePatrolCrewInput {
  userId: string
  displayName: string
  firstName: string | null
  lastName: string | null
  phone: string | null
  email: string
  membershipType: string | null
  status: string
}

export interface FirePatrolCandidate {
  userId: string
  displayName: string
  membershipType: string | null
}

const KEY = ['firePatrol'] as const

export function useFirePatrolCalendar(from: string, to: string) {
  return useQuery({
    queryKey: [...KEY, 'calendar', from, to],
    queryFn: () => api.get<FirePatrolCalendar>(`/api/fire-patrol/calendar?from=${from}&to=${to}`),
  })
}

export function useFirePatrolCrew(enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'crew'],
    queryFn: () => api.get<{ crew: FirePatrolCrewMember[] }>('/api/fire-patrol/crew').then((r) => r.crew),
    enabled,
  })
}

export function useFirePatrolCandidates(enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'candidates'],
    queryFn: () =>
      api.get<{ candidates: FirePatrolCandidate[] }>('/api/fire-patrol/crew/candidates').then((r) => r.candidates),
    enabled,
  })
}

// Everything on the page reads the same rows, so any change refreshes it all
// -- and the booking lists, since the aircraft bookings are shared with the
// main calendar.
function useRefresh() {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: KEY }),
      queryClient.invalidateQueries({ queryKey: ['bookings'] }),
    ])
}

export function useSignUp() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (values: { shiftDate: string; role: FirePatrolRole | null; note: string | null; userId?: string }) =>
      api.post<{ shiftId: number }>('/api/fire-patrol/shifts', values),
    onSuccess: refresh,
  })
}

export function useUpdateShift() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: ({ shiftId, ...patch }: { shiftId: number; role?: FirePatrolRole | null; note?: string | null; locked?: boolean }) =>
      api.patch(`/api/fire-patrol/shifts/${shiftId}`, patch),
    onSuccess: refresh,
  })
}

export function useDeleteShift() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (shiftId: number) => api.delete(`/api/fire-patrol/shifts/${shiftId}`),
    onSuccess: refresh,
  })
}

export function useSetWeekDispatcher() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: ({ weekStart, dispatcherUserId }: { weekStart: string; dispatcherUserId: string | null }) =>
      api.put(`/api/fire-patrol/weeks/${weekStart}`, { dispatcherUserId }),
    onSuccess: refresh,
  })
}

export interface BookAircraftValues {
  registration: string
  fromDate: string
  toDate: string
  startTime: string
  endTime: string
  estimatedFlightHours: number
  comment: string | null
}

export interface BookAircraftResult {
  created: { date: string; bookingId: number; kind: string }[]
  skipped: { date: string; reason: string }[]
  // Ordinary bookings moved to standby because the patrol takes precedence.
  displaced: number[]
}

export function useBookAircraft() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (values: BookAircraftValues) => api.post<BookAircraftResult>('/api/fire-patrol/bookings', values),
    onSuccess: refresh,
  })
}

export interface ReleaseResult {
  cancelled: number[]
  // Standbys the release gave back to their owners as bookings.
  promoted: number[]
}

export function useReleaseBooking() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: ({ bookingId, reason }: { bookingId: number; reason?: string }) =>
      api.delete<ReleaseResult>(`/api/fire-patrol/bookings/${bookingId}`, { reason }),
    onSuccess: refresh,
  })
}

export function useReleaseRun() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: ({ bulkGroupId, reason }: { bulkGroupId: string; reason?: string }) =>
      api.delete<ReleaseResult>(`/api/fire-patrol/bookings/groups/${bulkGroupId}`, { reason }),
    onSuccess: refresh,
  })
}

export function useSaveCrewMember() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: ({ userId, ...crew }: FirePatrolCrewInput & { userId: string }) =>
      api.put(`/api/fire-patrol/crew/${userId}`, crew),
    onSuccess: refresh,
  })
}

export function useRemoveCrewMember() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (userId: string) => api.delete(`/api/fire-patrol/crew/${userId}`),
    onSuccess: refresh,
  })
}
